"use client";

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import FocusLock from "react-focus-lock";

import CurrencyButton from "@/components/HeaderSettings/CurrencyButton";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { NavigationContext } from "@/context/NavigationContext";
import { UserContext } from "@/context/UserContext";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import { DL_KEYS } from "@/dataLayer/keys";
import useBreakPoint from "@/hooks/useBreakpoint";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import { HeaderSettingsProps } from "@/types";

const LanguageSettings: FC<HeaderSettingsProps> = ({ settings }) => {
  const t = useTranslations();
  const { languagesVisible, setLanguagesVisible, setTransitionComplete } = useContext(NavigationContext);
  const { language, changeLanguage } = useContext(UserContext);
  const [trigger, setTrigger] = useState<HTMLElement>();
  const panelRef = useRef<HTMLDivElement>(null);
  const isSo = useIsSaleScreen();

  const { isMd } = useBreakPoint("md");

  const keyboardHandler = useCallback(
    (event: KeyboardEvent) => {
      if (languagesVisible && event.key.toLowerCase() === "escape") {
        setLanguagesVisible(false);
      }
    },
    [languagesVisible, setLanguagesVisible],
  );

  useEffect(() => {
    if (document) {
      document.addEventListener("keydown", keyboardHandler);
      return () => {
        document.removeEventListener("keydown", keyboardHandler);
      };
    }
  }, [keyboardHandler]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef?.current?.contains(event.target as HTMLElement)) {
        setLanguagesVisible(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [panelRef, setLanguagesVisible, languagesVisible]);

  const overlayVariant = {
    open: { opacity: 1, display: "block" },
    close: { opacity: 0, transitionEnd: { display: "none" } },
  };

  const popupVariant = {
    open: { y: "0%", display: "block", transition: { ease: "easeOut", duration: 0.3 } },
    close: (breakpoint: boolean) => {
      return {
        y: breakpoint ? "30%" : "100%",
        transition: { ease: "easeIn", duration: 0.3 },
      };
    },
  };

  const changeLanguageCallBack = useCallback(
    (item: string) => {
      changeLanguage(item);
      if (isMd) {
        setLanguagesVisible(false);
      }
    },
    [isMd, changeLanguage, setLanguagesVisible],
  );

  return (
    <motion.div
      id="modal-language"
      variants={overlayVariant}
      initial="close"
      animate={languagesVisible ? "open" : "close"}
      className={`fixed left-0 top-0 md:top-[28px] w-screen h-dvh md:h-auto bg-[rgba(0,0,0,0.3)] md:bg-transparent z-50`}
      onAnimationComplete={() => {
        setTransitionComplete(true);
      }}
      onAnimationStart={() => {
        setTransitionComplete(false);
      }}
    >
      <FocusLock
        // group="menu"
        autoFocus={true}
        returnFocus={(el) => {
          if (!trigger) {
            setTrigger(el as HTMLElement);
          } else {
            trigger.focus();
            setTrigger(undefined);
          }
          return true;
        }}
        disabled={!languagesVisible}
        className="w-full h-full md:h-auto"
      >
        <div
          style={isSo ? { justifyContent: "center", transform: "translateX(-1.2%)" } : {}}
          className={`w-full h-full md:h-auto flex items-end md:items-center md:container`}
        >
          <div className="w-full md:w-fit">
            <div className="mx-auto header-modal w-full md:w-fit md:min-w-[200px]">
              <motion.div
                ref={panelRef}
                initial={"close"}
                variants={popupVariant}
                custom={isMd}
                animate={languagesVisible ? "open" : "close"}
                className="bg-white px-m py-s rounded-t-[30px] md:rounded-lg relative shadow-strong"
              >
                <Button
                  className="modal__close md:hidden"
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.TERTIARY}
                  theme={ButtonTheme.BLACK}
                  onClick={() => {
                    setLanguagesVisible(false);
                    // ctaClickTracking(t("button.label.closeModal"), "LanguageSettings", "LanguageSettings");
                    ctaClickTracking(DL_KEYS?.close_modal, "LanguageSettings", "LanguageSettings");
                  }}
                >
                  <span>
                    <Icon name="close" size="24" title="close" />
                    <span className="sr-only">{t("button.label.closeModal")}</span>
                  </span>
                </Button>
                <div className="bg-gray-2 mx-auto w-[36px] h-[5px] rounded md:hidden"></div>
                <p className="md:hidden p-m font-primary text-m uppercase font-medium">{t("header.labels.language")}</p>
                <div className="flex flex-col gap-s">
                  {settings?.languages?.map((item) => {
                    return (
                      <CurrencyButton
                        clickHandler={() => {
                          if (item != language) {
                            changeLanguageCallBack(item);
                          }
                        }}
                        selected={item === language}
                        key={`header-language-${item}`}
                        label={t(`header.language.${item}`)}
                        customClass="text-s"
                      />
                    );
                  })}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FocusLock>
    </motion.div>
  );
};

export default LanguageSettings;
